/**
 * Keeps `body.dark-theme` / `body.light-theme` and MDS design-token stylesheets
 * aligned with `mc-theme-switch` (including before the switch mounts on first load).
 *
 * Storage key matches `@maersk-global/mds-components-utils` localStore
 * (`LSThemeCategory` + `LSThemeKey` on `mc-theme-switch`).
 */
export const MDS_THEME_LOCAL_STORAGE_KEY = "[mds-theme-switch]theme";

const LIGHT_MEDIA = "(prefers-color-scheme: light)";
const DARK_MEDIA = "(prefers-color-scheme: dark)";

export type MdsThemeSwitchDetail = "auto" | "dark" | "light";

export function isMdsThemeDark(
  theme: string | undefined,
): boolean {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function readPersistedMdsTheme(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(MDS_THEME_LOCAL_STORAGE_KEY);
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : null;
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
}

function getMdsDesignTokenLinks(): {
  light: HTMLLinkElement | null;
  dark: HTMLLinkElement | null;
} {
  if (typeof document === "undefined") {
    return { light: null, dark: null };
  }

  const light =
    document.querySelector<HTMLLinkElement>(
      'link[rel="stylesheet"][media*="prefers-color-scheme"][media*="light"]',
    ) ??
    document.querySelector<HTMLLinkElement>(
      'link[href*="apmterminals/light"][href*="design-tokens"]',
    );

  const dark =
    document.querySelector<HTMLLinkElement>(
      'link[rel="stylesheet"][media*="prefers-color-scheme"][media*="dark"]',
    ) ??
    document.querySelector<HTMLLinkElement>(
      'link[href*="apmterminals/dark"][href*="design-tokens"]',
    );

  return { light, dark };
}

/**
 * Mirrors `McThemeSwitch.switchTheme` — token `<link>` media must match the saved
 * theme before the web component connects (lazy-loaded app shell).
 */
export function syncMdsDesignTokenLinks(theme: string | undefined): void {
  if (typeof document === "undefined") return;

  const { light: styleLight, dark: styleDark } = getMdsDesignTokenLinks();
  if (!styleLight || !styleDark) return;

  const t = theme ?? "auto";
  let lightMedia: string;
  let darkMedia: string;

  if (t === "auto") {
    lightMedia = LIGHT_MEDIA;
    darkMedia = DARK_MEDIA;
    document.documentElement.style.removeProperty("color-scheme");
  } else if (t === "dark") {
    lightMedia = "not all";
    darkMedia = "all";
    document.documentElement.style.setProperty("color-scheme", "dark");
  } else {
    lightMedia = "all";
    darkMedia = "not all";
    document.documentElement.style.setProperty("color-scheme", "light");
  }

  styleLight.media = lightMedia;
  styleDark.media = darkMedia;
}

export function syncBodyClassesFromMdsTheme(
  theme: string | undefined,
): void {
  if (typeof document === "undefined") return;

  const dark = isMdsThemeDark(theme);
  if (dark) {
    document.body.classList.add("dark-theme");
    document.body.classList.remove("light-theme");
  } else {
    document.body.classList.add("light-theme");
    document.body.classList.remove("dark-theme");
  }
}

/** Body classes + design-token stylesheets — single entry for boot and theme changes. */
export function applyMdsTheme(theme: string | undefined): void {
  syncBodyClassesFromMdsTheme(theme);
  syncMdsDesignTokenLinks(theme);
}

/** Applies saved theme (or `"auto"` when unset) — call on boot; do not rely on `change` from `connectedCallback` (fires before React binds). */
export function syncBodyThemeOnLoad(): void {
  const saved = readPersistedMdsTheme();
  const theme = saved ?? "auto";
  applyMdsTheme(theme);
}

let mdsBodyThemeMediaListenerInstalled = false;

/** When preference is `auto`, keep body classes in sync if the OS scheme changes. */
export function installMdsBodyThemeMediaListener(): void {
  if (typeof window === "undefined" || mdsBodyThemeMediaListenerInstalled) return;
  mdsBodyThemeMediaListenerInstalled = true;
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const t = readPersistedMdsTheme() ?? "auto";
      if (t === "auto") applyMdsTheme("auto");
    });
}
